/* eslint-disable no-underscore-dangle */
/* eslint-disable @typescript-eslint/explicit-module-boundary-types, @typescript-eslint/no-unused-vars */

import { HttpClient, HttpParams } from '@angular/common/http';
import { AbstractControl, UntypedFormControl, ValidationErrors } from '@angular/forms';
import { FormlyFieldConfig } from '@ngx-formly/core';
import { Observable, of } from 'rxjs';
import { map } from 'rxjs/operators';
import { LoadingService } from '@app/core/services';
import * as Transforms from '@app/utils/transforms';

export const duplicateValidationMessage = (err: any, field: FormlyFieldConfig) => {
	const fieldName = field.props.label || field.key || 'value';
	return `A record with this ${fieldName} already exists`;
};

export const emailValidationMessage = (err: any, field: any) => `Invalid email address`;

export const minDateValidationMessage = (err: any, field: any) => `This value should be more than ${Transforms.toShortDate(err.min)}`;

export const maxDateValidationMessage = (err: any, field: any) => `This value should be less than ${Transforms.toShortDate(err.max)}`;

export const minLengthValidationMessage = (err: any, field: any) => `Should have at least ${field.templateOptions.minLength} characters`;

export const maxLengthValidationMessage = (err: any, field: any) => `This value should be less than ${field.templateOptions.maxLength} characters`;

export const minValidationMessage = (err: any, field: any) => `This value should be equal or more than ${field.templateOptions.min}`;

export const maxValidationMessage = (err: any, field: any) => `This value should be equal or less than ${field.templateOptions.max}`;
export const phoneNumberValidationMessage = (err: any, field: any) => `Invalid phone number`;

export const maxDecimalPlacesValidationMessage = (err: any, field: any) =>
	`Should be max of ${field.templateOptions.maxDecimalPlaces} decimal places`;

export const maskValidationMessage = (err: any, field: FormlyFieldConfig) => {
	const fieldName = field.props.label || field.key || 'This field';
	return `${fieldName} is invalid`;
};

export const requiredValidationMessage = (err: any, field: FormlyFieldConfig) => {
	const fieldName = field.props.label || field.key || 'This field';
	return `${fieldName} is required`;
};

export const hasBegEndWhitespaceMessage = (err: any, field: FormlyFieldConfig) => {
	const fieldName = field.props.label || field.key || 'This field';
	return `${fieldName} must not begin or end with blank space`;
};

export const lettersOnlyMessage = (err: any, field: FormlyFieldConfig) => {
	const fieldName = field.props.label || field.key || 'This field';
	return `${fieldName} should contain only letters`;
};

export const notGreaterThanZeroMessage = (err: any, field: FormlyFieldConfig) => {
	const fieldName = field.props.label || field.key || 'This field';
	return `${fieldName} must be greater than zero`;
};

export const minTimeValidationMessage = (err: any, field: FormlyFieldConfig) => {
	const fieldName = field.props.label || field.key || 'This field';
	return `${fieldName} cannot be before start time`;
};

export const maxTimeValidationMessage = (err: any, field: FormlyFieldConfig) => {
	const fieldName = field.props.label || field.key || 'This field';
	return `${fieldName} cannot be after end time`;
};

export const duplicateValidator = (control: UntypedFormControl, field: FormlyFieldConfig) => {
	const values = typeof field.props.otherValues === 'function' ? field.props.otherValues() : field.props.otherValues;
	if (values?.some((item) => item.trim().localeCompare(control.value?.trim(), undefined, { sensitivity: 'accent' }) === 0)) {
		return { duplicate: true };
	}
	return null;
};

export const maxDecimalPlacesValidator = (control: UntypedFormControl, field: FormlyFieldConfig) => {
	if (control.value && +parseFloat(control.value).toFixed(field.props.maxDecimalPlaces) !== +control.value) {
		return { maxDecimalPlaces: true };
	}
	return null;
};

export const notGreaterThanZeroValidator = (control: UntypedFormControl) => {
	if (control.value && parseFloat(control.value) <= 0) {
		return { notGreaterThanZero: true };
	}
	return null;
};

export const whiteSpaceValidator = (control: UntypedFormControl, field: FormlyFieldConfig) => {
	if (!control.value || (typeof control.value === 'string' && control.value.trim() === '')) {
		return { whitespace: true };
	}
	return null;
};

export const hasBegEndWhiteSpaceValidator = (control: UntypedFormControl, field: FormlyFieldConfig) => {
	if (control.value && typeof control.value === 'string' && control.value.length !== control.value.trim().length) {
		return { hasBegEndWhitespace: true };
	}
	return null;
};

export const lettersOnlyValidator = (control: UntypedFormControl, field: FormlyFieldConfig) => {
	if (control.value && !/^[a-zA-Z\s]*$/.test(control.value)) {
		return { lettersOnly: true };
	}
	return null;
};

export const phoneNumberValidator = (control: UntypedFormControl) => {
	if (control.value && !/^\(?([0-9]{3})\)?[-. ]?([0-9]{3})[-. ]?([0-9]{4})$/.test(control.value)) {
		return { phoneNumber: true };
	}
	return null;
};

export const duplicateAsyncValidator =
	(http: HttpClient, loadingService: LoadingService) =>
	(control: UntypedFormControl, field: FormlyFieldConfig, options: any = {}): Observable<ValidationErrors> => {
		const value = control.value;
		const url = options.url;
		const key = options.key || field.key;

		// disable this validation based templateOptions's duplicateAsync
		if (!field.props?.duplicateAsync) {
			return of(null);
		}

		// API Route: `${url}?${key}=${value}`
		// where: url = 'api/fa/asset/validate', key = 'AssetNumber', value = '123'
		// then: 'api/fa/asset/validate?AssetNumber=123'
		const params = new HttpParams().set(key.toString(), value);

		loadingService.skipNextRequest();

		return http
			.get(url, {
				params,
			})
			.pipe(
				map((response) => {
					if (response) {
						return {
							duplicateAsync: response,
						};
					}
					return null;
				})
			);
	};

export const duplicateAsyncMessage = (err: any, field: FormlyFieldConfig): string => {
	const fieldName = field.props.label || field.key || 'value';
	return `A record with this ${fieldName} already exists`;
};

export const matchValidator = (control: AbstractControl, _, options) => {
	const { fields } = options;

	if (fields.length < 2) {
		return null;
	}

	const values = fields.map((field) => control?.value?.[field]);

	const allMatching = values.every((value) => value === values[0]);
	if (allMatching) {
		return null;
	}

	const controls = fields.map((field) => control.get(field));

	controls.forEach((c) => c.markAsTouched());

	const labels = controls.map((c) => c?._fields?.[0]?.templateOptions?.label);

	return {
		match: labels,
	};
};

export const matchMessage = (err: any): string => {
	const labels = Object.values(err).join(', ');
	return `The values of ${labels} must match`;
};

export const routingNumberValidator = (control: UntypedFormControl) => {
	const value = control.value;
	if (!value || (typeof value === 'string' && value.length === 0)) {
		// Ignore empty values, use required validator for that
		return null;
	}

	if (value && !/^\d{9}$/.test(value)) {
		return { routingNumber: true };
	}

	let sum = 0;
	for (let i = 0; i < value.length; i += 3) {
		sum += parseInt(value.charAt(i), 10) * 3
			+ parseInt(value.charAt(i + 1), 10) * 7
			+ parseInt(value.charAt(i + 2), 10);
	}

	if (sum === 0 || sum % 10 !== 0) {
		return { routingNumber: true };
	}

	return null;
};

export const routingNumberMessage = (err: any, field: FormlyFieldConfig) => {
	const fieldName = field.props.label || field.key || 'This field';
	return `${fieldName} is not a valid routing number`;
};
